/**
 * DesignPreview — /design-preview
 * Internal page to verify visual hierarchy across ContentCard variants
 * and hero text-wrapper readability.
 */
import { ContentCard } from '@/components/ui/content-card';
import heroImage from '@/assets/landing-hero-landscape.jpg';

const SAMPLE_IMAGE = 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=640&q=80';

export default function DesignPreview() {
  return (
    <div className="min-h-screen bg-background py-12 px-5">
      <div className="max-w-5xl mx-auto">
        <h1 className="font-display text-2xl font-medium text-foreground mb-2">
          Visual Hierarchy Preview
        </h1>
        <p className="text-sm text-muted-foreground mb-12">
          Place (4:3, largest) → Experience (3:2) → Producer (3:2, compact) → Event (3:2, date)
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 items-start">
          {/* Place */}
          <div>
            <span className="text-xs text-muted-foreground/60 uppercase tracking-widest mb-3 block">
              Place (4:3)
            </span>
            <ContentCard
              variant="place"
              title="Gröna Dalen"
              subtitle="Sörmland"
              imageUrl={SAMPLE_IMAGE}
              href="#"
            />
          </div>

          {/* Experience */}
          <div>
            <span className="text-xs text-muted-foreground/60 uppercase tracking-widest mb-3 block">
              Experience (3:2)
            </span>
            <ContentCard
              variant="experience"
              title="Vara tillsammans"
              subtitle="Platser för lugna möten"
              description="Platser där ni kan vara nära, utan att det behöver vara stort."
              imageUrl={SAMPLE_IMAGE}
              href="#"
            />
          </div>

          {/* Producer */}
          <div>
            <span className="text-xs text-muted-foreground/60 uppercase tracking-widest mb-3 block">
              Producer (compact)
            </span>
            <ContentCard
              variant="producer"
              title="Ekbackens Gård"
              subtitle="Östergötland"
              description="En liten familjegård med fokus på ekologisk odling och lokal mat sedan tre generationer."
              imageUrl={SAMPLE_IMAGE}
              href="#"
            />
          </div>

          {/* Event */}
          <div>
            <span className="text-xs text-muted-foreground/60 uppercase tracking-widest mb-3 block">
              Event (date)
            </span>
            <ContentCard
              variant="event"
              title="Kvällsprovning: lokala ostar"
              subtitle="Ekbackens Gård"
              date="15 mar"
              imageUrl={SAMPLE_IMAGE}
              href="#"
            />
          </div>
        </div>

        {/* Without images */}
        <h2 className="font-display text-lg font-medium text-foreground mt-16 mb-6">
          Utan bild
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 items-start">
          <ContentCard variant="place" title="Plats utan bild" subtitle="Dalarna" href="#" />
          <ContentCard variant="experience" title="Upplevelse utan bild" description="Kort beskrivning av upplevelsen." href="#" />
          <ContentCard variant="producer" title="Producent utan bild" description="Beskrivning av producenten och deras verksamhet." href="#" />
          <ContentCard variant="event" title="Event utan bild" date="22 apr" subtitle="Värdnamn" href="#" />
        </div>

        {/* Hero text-wrapper comparison */}
        <h2 className="font-display text-lg font-medium text-foreground mt-16 mb-6">
          Hero: utan vs med text-wrapper
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Without wrapper */}
          <div>
            <span className="text-xs text-muted-foreground/60 uppercase tracking-widest mb-3 block">
              Utan wrapper (gammal)
            </span>
            <div
              className="relative rounded-xl overflow-hidden"
              style={{ height: '260px' }}
            >
              <img
                src={heroImage}
                alt=""
                className="absolute inset-0 w-full h-full object-cover saturate-[0.7]"
              />
              <div className="absolute inset-0 bg-background/[0.62]" />
              <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-background to-transparent" />
              <div className="relative z-10 flex items-end h-full p-6">
                <div>
                  <p className="font-display text-xl font-medium text-foreground/85">
                    Ett stillsamt sätt att närma sig
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Allt här är inte för alla.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* With wrapper */}
          <div>
            <span className="text-xs text-muted-foreground/60 uppercase tracking-widest mb-3 block">
              Med wrapper (ny)
            </span>
            <div
              className="relative rounded-xl overflow-hidden"
              style={{ height: '260px' }}
            >
              <img
                src={heroImage}
                alt=""
                className="absolute inset-0 w-full h-full object-cover saturate-[0.7]"
              />
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background to-transparent" />
              <div className="relative z-10 flex items-end h-full p-6">
                <div className="bg-background/[0.91] backdrop-blur-[3px] rounded-xl px-5 py-5">
                  <p className="font-display text-xl font-medium text-foreground">
                    Ett stillsamt sätt att närma sig
                  </p>
                  <p className="mt-2 text-sm text-foreground/[0.65]">
                    Allt här är inte för alla.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
